/**
 * useLinksOutsideFilter Composable
 *
 * Which targets of a filtered link field its filter no longer offers. A
 * narrower filter never removes links; they stay, and their chips show a
 * warning until someone fixes them. One query per field, shared through the
 * Apollo cache by every cell and block showing that field.
 *
 * @example
 * const { isOutside } = useLinksOutsideFilter(() => props.field)
 * isOutside(entity.id) // true when the field's filter wouldn't offer it
 */

import { computed, type MaybeRefOrGetter, toValue } from "vue"
import { useQuery } from "@vue/apollo-composable"
import { hasActiveConditions, type SavedFilter } from "@auger/core/fields"
import { fetchLinksOutsideFilter } from "@/common/hasura/links"
import { getSchema } from "@/stores/schemas"

type FilteredField = {
  type?: string
  key?: string
  sourceKey?: string
  recordType?: string | null
  filter?: SavedFilter
  schema_id?: number
}

export function useLinksOutsideFilter(field: MaybeRefOrGetter<FilteredField | null | undefined>) {
  const variables = computed(() => {
    const f = toValue(field)
    if (f?.type !== "link" || !f.recordType || !hasActiveConditions(f.filter)) return null
    const sourceType = f.schema_id != null ? getSchema(f.schema_id)?.record_type : null
    if (!sourceType) return null
    return {
      sourceType,
      fieldKey: f.sourceKey ?? f.key ?? "",
      targetType: f.recordType,
      filter: f.filter,
    }
  })

  const { result } = useQuery(
    fetchLinksOutsideFilter,
    () =>
      variables.value ?? {
        sourceType: "",
        fieldKey: "",
        targetType: "",
        filter: { conditions: [] },
      },
    () => ({ enabled: !!variables.value, fetchPolicy: "cache-first" })
  )

  const outside = computed(
    () => new Set((result.value?.links ?? []).map((l) => String(l.linked_id)))
  )

  return {
    isOutside: (id: number | string) => outside.value.has(String(id)),
  }
}
